import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { CalculatedCounts } from './types';
import InfoTooltip from '@/shared/components/forms/InfoTooltip';

interface SplitTableProps {
  trainPercentage: number;
  valPercentage: number;
  testPercentage: number;
  randomDat: boolean;
  calculatedCounts: CalculatedCounts;
  isInvalidSum: boolean;
  isTrainEmpty: boolean;
  isValEmpty: boolean;
  totalPercentage: number;
  disabled: boolean;
  onTrainChange: (value: number) => void;
  onValChange: (value: number) => void;
  onRandomChange: (value: boolean) => void;
}

const inputBase = 'w-16 sm:w-20 text-center text-xs sm:text-sm border rounded-lg px-1.5 py-1.5 sm:px-2 sm:py-2 text-foreground bg-card disabled:bg-muted disabled:text-muted-foreground transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-ring/30 focus:border-ring [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none';
const inputValid = 'border-input';
const inputError = 'border-red-300 bg-red-50/30';

/**
 * SplitTable sub-component
 * Renders the data split configuration as card-based rows
 */
export const SplitTable: React.FC<SplitTableProps> = memo(({
  trainPercentage,
  valPercentage,
  testPercentage,
  randomDat,
  calculatedCounts,
  isInvalidSum,
  isTrainEmpty,
  isValEmpty,
  totalPercentage,
  disabled,
  onTrainChange,
  onValChange,
  onRandomChange
}) => {
  const { t } = useTranslation('training');
  const { n_train, n_val, n_test } = calculatedCounts;

  return (
    <div className="rounded-lg border border-border overflow-visible">
      {/* Header */}
      <div className="grid grid-cols-3 bg-muted/80 border-b border-border">
        <div className="px-3 py-2 sm:px-4 sm:py-2.5" />
        <div className="px-3 py-2 sm:px-4 sm:py-2.5 text-center">
          <span className="text-[10px] sm:text-xs uppercase tracking-wide font-medium text-muted-foreground">
            {t('training.dataSplit.percentageColumn')}
          </span>
        </div>
        <div className="px-3 py-2 sm:px-4 sm:py-2.5 text-center">
          <span className="text-[10px] sm:text-xs uppercase tracking-wide font-medium text-muted-foreground">
            {t('training.dataSplit.countColumn')}
          </span>
        </div>
      </div>

      {/* Train Row */}
      <div className="grid grid-cols-3 items-center border-b border-border">
        <div className="px-3 py-2.5 sm:px-4 sm:py-3">
          <div className="flex items-center gap-1">
            <label
              htmlFor="data-split-train-percentage"
              className="text-xs sm:text-sm font-medium text-foreground"
            >
              {t('training.dataSplit.train')}
            </label>
            {isTrainEmpty && <span className="text-red-400 ml-0.5">*</span>}
            <InfoTooltip text={t('tooltips.datensatzAufteilung.trainieren')} width="w-72" />
          </div>
        </div>
        <div className="px-3 py-2.5 sm:px-4 sm:py-3 flex flex-col items-center">
          <div className="inline-flex items-center gap-1">
            <input
              id="data-split-train-percentage"
              type="number"
              value={trainPercentage === 0 ? '' : trainPercentage}
              onChange={(e) => {
                const val = e.target.value;
                onTrainChange(val ? Number(val) : 0);
              }}
              disabled={disabled}
              placeholder=""
              className={`${inputBase} ${isInvalidSum || isTrainEmpty ? inputError : inputValid}`}
              min="0"
              max="100"
              step="0.1"
            />
            <span className="text-xs text-muted-foreground">%</span>
          </div>
          {isTrainEmpty && (
            <p className="text-red-500 text-[10px] mt-1">
              {t('training.validation.required', 'Required')}
            </p>
          )}
        </div>
        <div className="px-3 py-2.5 sm:px-4 sm:py-3 text-center">
          <span className="text-xs sm:text-sm font-mono tabular-nums text-primary font-medium">{n_train}</span>
        </div>
      </div>

      {/* Validation Row */}
      <div className="grid grid-cols-3 items-center border-b border-border">
        <div className="px-3 py-2.5 sm:px-4 sm:py-3">
          <div className="flex items-center gap-1">
            <label
              htmlFor="data-split-val-percentage"
              className="text-xs sm:text-sm font-medium text-foreground"
            >
              {t('training.dataSplit.validate')}
            </label>
            {isValEmpty && <span className="text-red-400 ml-0.5">*</span>}
            <InfoTooltip text={t('tooltips.datensatzAufteilung.validieren')} width="w-72" />
          </div>
        </div>
        <div className="px-3 py-2.5 sm:px-4 sm:py-3 flex flex-col items-center">
          <div className="inline-flex items-center gap-1">
            <input
              id="data-split-val-percentage"
              type="number"
              value={valPercentage === 0 ? '' : valPercentage}
              onChange={(e) => {
                const val = e.target.value;
                onValChange(val ? Number(val) : 0);
              }}
              disabled={disabled}
              placeholder=""
              className={`${inputBase} ${isInvalidSum || isValEmpty ? inputError : inputValid}`}
              min="0"
              max="100"
              step="0.1"
            />
            <span className="text-xs text-muted-foreground">%</span>
          </div>
          {isValEmpty && (
            <p className="text-red-500 text-[10px] mt-1">
              {t('training.validation.required', 'Required')}
            </p>
          )}
        </div>
        <div className="px-3 py-2.5 sm:px-4 sm:py-3 text-center">
          <span className="text-xs sm:text-sm font-mono tabular-nums text-primary font-medium">{n_val}</span>
        </div>
      </div>

      {/* Test Row (computed) */}
      <div className={`grid grid-cols-3 items-center border-b border-border ${isInvalidSum ? 'bg-red-50/40' : 'bg-muted/30'}`}>
        <div className="px-3 py-2.5 sm:px-4 sm:py-3">
          <div className="flex items-center gap-1">
            <span className="text-xs sm:text-sm font-medium text-foreground">
              {t('training.dataSplit.test')}
            </span>
            <InfoTooltip text={t('tooltips.datensatzAufteilung.testen')} width="w-72" />
          </div>
        </div>
        <div className="px-3 py-2.5 sm:px-4 sm:py-3 flex flex-col items-center">
          <span className={`text-xs sm:text-sm ${isInvalidSum ? 'text-red-600 font-semibold' : 'text-muted-foreground'}`}>
            {testPercentage.toFixed(1)}%
          </span>
          <span className="text-[10px] text-muted-foreground mt-0.5">
            ({t('training.dataSplit.automatic')})
          </span>
          {isInvalidSum && (
            <p className="text-red-500 text-[10px] mt-1 font-medium">
              Total: {totalPercentage.toFixed(1)}% (max 100%)
            </p>
          )}
        </div>
        <div className="px-3 py-2.5 sm:px-4 sm:py-3 text-center">
          <span className="text-xs sm:text-sm font-mono tabular-nums text-primary font-medium">{n_test}</span>
        </div>
      </div>

      {/* Randomization Row */}
      <div className="px-3 py-3 sm:px-4 sm:py-3.5 flex items-center justify-center gap-2">
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={randomDat}
            onChange={(e) => onRandomChange(e.target.checked)}
            disabled={disabled}
            className="w-3.5 h-3.5 rounded border-input accent-primary disabled:opacity-40"
          />
          <span className="text-xs sm:text-sm text-muted-foreground">
            {t('training.dataSplit.shuffleData')}
          </span>
        </label>
        <InfoTooltip text={t('tooltips.datensatzAufteilung.shuffle')} width="w-80" />
      </div>
    </div>
  );
});

SplitTable.displayName = 'SplitTable';

export default SplitTable;
